/** @jsx jsx */
import { Box, EmptyState, EmptyStateListWhite, Heading, Inline, Status } from "@eworkit/iris";
import { TFunction } from "i18next";
import { isEmpty } from "lodash-es";
import React, { FC, memo, useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useMedia } from "react-use";
import { jsx } from "theme-ui";

import { Button } from "@/components/Button";
import { FiltersButtons } from "@/components/Filters";
import { LoadingSpinner } from "@/components/Loading";
import { Section } from "@/components/Section";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_MD, MEDIA_SIZE_SM } from "@/core/media";
import { IURLSortConfig } from "@/core/url";
import { ITask, ITasksListSearchParams, ITasksURLFilter, TaskActionType } from "@/data/tasks";
import { ITasksRouteParams, tasksListSearchParamsType, tasksPatchParamsType } from "@/data/tasks/filters";
import { fetchTasks, putTaskAction } from "@/data/tasks/service";
import { IPageableTask } from "@/data/tasks/types";
import { Flex, Text, TextInput } from "@/eworkitComponents";
import { useBoundingClientRect } from "@/hooks/BoundingClientRect";
import { TPageableFetchFunction, useMetaPageableFetcher } from "@/hooks/http";
import { useDebounce } from "@/hooks/Timeout";
import { mapToQuery } from "@/infrastructure/Filter/service";
import { TaskContext } from "@/infrastructure/Tasks";
import { useRouter } from "@/router";
import { Container } from "@/views/components/Container";
import { InfoboxSection } from "@/views/components/Page";
import { useToasts } from "@/views/components/Toast/useToasts";
import { JobRequestDetailBackground } from "@/views/JobRequests/Detail/components";
import { router } from "@/views/router";
import { TaskDetails } from "@/views/Tasks/List/components/TaskDetails";
import { TaskList } from "@/views/Tasks/List/components/TaskList";

import { PageHeader } from "../../components/PageHeader";
import { Filters, tasksFiltersMapper, useTasksFilters } from "./components/Filters";

function getTaskActionMessage(t: TFunction, actionType: TaskActionType): string {
  switch (actionType) {
    case "accept":
      return t("TASKS.DETAILS.TASK_CONFIRMED_MESSAGE");
    case "cancel":
    case "reject":
      return t("TASKS.DETAILS.TASK_REJECTED_MESSAGE");
  }
}

export interface ITasksPageProps {}

export const TasksPage: FC<ITasksPageProps> = memo(() => {
  const { t } = useTranslation("session");
  const { contextCompany } = useContext(UserContext);
  const { addToast } = useToasts();
  const { refresh: taskContextRefresh } = useContext(TaskContext);
  const history = useHistory();
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const isMediumScreen = useMedia(MEDIA_SIZE_MD);
  const [{ boxRef, containerRef }, { top }] = useBoundingClientRect();
  const debounceTime = 600;

  const {
    searchParams: meta,
    replaceSearchParams: changeMeta,
    matchParams,
    replace,
  } = useRouter<ITasksRouteParams, ITasksListSearchParams>({
    ensureSearchParams: tasksListSearchParamsType,
    ensureMatchParams: tasksPatchParamsType,
  });

  const fetchTasksList: TPageableFetchFunction<ITask, IPageableTask, ITasksURLFilter, IURLSortConfig[]> = useCallback(
    (params, config) => {
      const { filtersConfig, ...restParams } = params;
      return fetchTasks(
        {
          ...restParams,
          companyId: contextCompany?.id,
          ...mapToQuery(tasksFiltersMapper, filtersConfig || {}),
        },
        config,
      );
    },
    [contextCompany?.id],
  );

  const debouncedFilters = useDebounce(meta.filtersConfig, debounceTime);

  const mappedMeta = useMemo(() => {
    return {
      ...meta,
      filtersConfig: debouncedFilters,
    };
  }, [meta, debouncedFilters]);

  const { working, payload, changeFilter, fetch, changePage, counter } = useMetaPageableFetcher(
    fetchTasksList,
    { meta: mappedMeta, changeMeta },
    [JSON.stringify(mappedMeta), fetchTasksList],
  );

  const { isFilterOpen, toggleFilterOpen, handleFiltersChange, clearFilters } = useTasksFilters(
    meta.filtersConfig,
    changeFilter,
  );

  const tasks: ITask[] = payload?.content.map(task => task.data) ?? [];

  const activeTask: ITask | undefined = useMemo(() => {
    const task = tasks.find((ta: ITask) => ta.id === matchParams.taskId);

    return task ? task : tasks.length ? tasks[0] : undefined;
  }, [matchParams.taskId, tasks]);

  const noTasks = useMemo(() => {
    return !working && !tasks.length;
  }, [working, tasks]);

  const noMatchingTasks = useMemo(() => {
    return !working && !tasks.length && Object.keys(meta.filtersConfig).length !== 0;
  }, [working, tasks, meta.filtersConfig]);

  useEffect(() => {
    if (!!matchParams.taskId && activeTask && activeTask.id !== matchParams.taskId) {
      replace(router.get("tasks", { taskId: activeTask.id }, meta).path);
    }
  }, [activeTask, matchParams.taskId, replace, meta]);

  const handleAction = useCallback(
    async (actionType: TaskActionType, comment: string, task: ITask) => {
      try {
        await putTaskAction(
          { companyId: contextCompany?.id, taskId: task.id },
          {
            action: actionType,
            comment,
          },
        );

        fetch();
        taskContextRefresh();

        addToast({
          text: getTaskActionMessage(t, actionType),
          type: "success",
        });
      } catch (error) {
        addToast({
          text: t("common:VALIDATION_MESSAGES.GENERAL"),
          type: "warning",
        });
      }
    },
    [fetch, addToast],
  );

  const handleBackToList = useCallback(() => {
    replace(router.get("tasks", { taskId: undefined }, meta).path);
  }, [replace, meta]);

  const isDetailsOpen = Number.isFinite(matchParams.taskId);

  if (working && !counter) {
    return (
      <Flex width={12} align="center" justify="center" margin={["xl", false]}>
        <LoadingSpinner size="lg" variant="primary" />
      </Flex>
    );
  }

  if (!noMatchingTasks && noTasks) {
    return (
      <Box sx={{ py: 120 }} px="sm">
        <EmptyState
          icon={<EmptyStateListWhite />}
          title={t("TASKS.NO_TASKS")}
          content={t("TASKS.NO_TASKS_DESCRIPTION")}
        />
      </Box>
    );
  }

  if (isMediumScreen) {
    return (
      <React.Fragment>
        {isDetailsOpen && !noMatchingTasks && <JobRequestDetailBackground />}

        {(!isDetailsOpen || noMatchingTasks) && (
          <PageHeader
            header={
              <Inline alignItems="center" gap="xs">
                <Heading size="h-md">{t("TASKS.PAGE_HEADER")}</Heading>
                <Status variant="secondary">{payload?.totalElements}</Status>
              </Inline>
            }>
            <Flex width={12} innerMargin="md">
              <TextInput
                width={12}
                noBorder={isSmallScreen}
                name="search"
                icon="search"
                size="lg"
                placeholder={t("TASKS.SEARCH_PLACEHOLDER")}
                value={meta.filtersConfig.query}
                onChange={query => handleFiltersChange({ query })}
              />
              <FiltersButtons
                isFilterOpen={isFilterOpen}
                onFilterOpenClick={toggleFilterOpen}
                onClearFilterClick={clearFilters}
                appearance={isSmallScreen ? "mobile" : "desktop"}
                hideClearFilters={isEmpty(meta.filtersConfig)}
              />
            </Flex>
          </PageHeader>
        )}

        <Flex direction="column" padding={[false, isSmallScreen ? false : "md"]}>
          {!isDetailsOpen && (
            <Filters
              isFilterOpen={isFilterOpen}
              onFiltersUpdate={handleFiltersChange}
              onFilterClear={clearFilters}
              filtersValue={meta.filtersConfig}
              resultsCount={payload?.numberOfElements}
            />
          )}

          {noMatchingTasks ? (
            <InfoboxSection
              heading={t("TASKS.NO_MATCHING_TASKS")}
              description={t("TASKS.NO_MATCHING_TASKS_DESCRIPTION")}
              sx={{ my: "sm" }}
            />
          ) : (
            <Flex width={12} direction="column" padding={[false, false, "md", false]}>
              {isDetailsOpen && (
                <Flex width={12} direction="column">
                  <Flex width={12} padding={isSmallScreen ? ["md", false, "md", "md"] : ["md", false]}>
                    <Button variant="transparent" icon="arrow_back" onClick={handleBackToList}>
                      <Text>{t("TASKS.BACK_TO_LIST")}</Text>
                    </Button>
                  </Flex>

                  {activeTask && <TaskDetails task={activeTask} refresh={fetch} onAction={handleAction} />}
                </Flex>
              )}

              {!isDetailsOpen && (
                <Flex width={12} style={{ height: "70vh" }} margin={["md", false, false, false]}>
                  <Section
                    width={12}
                    flex
                    direction="column"
                    background="background-card"
                    shadow={isSmallScreen ? "none" : "default"}
                    borderRadius={!isSmallScreen}
                    padding={["md", "md", "sm", "md"]}
                    margin={[false]}>
                    <TaskList
                      tasks={tasks}
                      working={working}
                      page={payload?.number}
                      pages={payload?.totalPages}
                      size={payload?.size}
                      total={payload?.totalElements}
                      onListItemClick={task => history.push(router.get("tasks", { taskId: task.id }, meta).path)}
                      onPageChange={changePage}
                    />
                  </Section>
                </Flex>
              )}
            </Flex>
          )}
        </Flex>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <PageHeader
        header={
          <Inline alignItems="center" gap="xs">
            <Heading size="h-md">{t("TASKS.PAGE_HEADER")}</Heading>
            <Status variant="secondary">{payload?.totalElements}</Status>
          </Inline>
        }>
        <Flex width={12} innerMargin="md">
          <TextInput
            width={12}
            name="search"
            icon="search"
            size="lg"
            placeholder={t("TASKS.SEARCH_PLACEHOLDER")}
            value={meta.filtersConfig.query}
            onChange={query => handleFiltersChange({ query })}
          />
          <FiltersButtons
            isFilterOpen={isFilterOpen}
            onFilterOpenClick={toggleFilterOpen}
            onClearFilterClick={clearFilters}
            appearance="desktop"
            hideClearFilters={isEmpty(meta.filtersConfig)}
          />
        </Flex>
      </PageHeader>

      <Container ref={containerRef} padding={[false, "md"]}>
        <Filters
          isFilterOpen={isFilterOpen}
          onFiltersUpdate={handleFiltersChange}
          onFilterClear={clearFilters}
          filtersValue={meta.filtersConfig}
          resultsCount={payload?.numberOfElements}
        />

        {noMatchingTasks ? (
          <InfoboxSection
            heading={t("TASKS.NO_MATCHING_TASKS")}
            description={t("TASKS.NO_MATCHING_TASKS_DESCRIPTION")}
            sx={{ my: "sm" }}
          />
        ) : (
          <Flex direction="row" innerMargin="md" padding={["md", false]} width={12}>
            <Flex ref={boxRef} width={{ xxs: 4 }} style={{ height: `calc(100vh - ${top}px)` }}>
              <Section
                width={12}
                flex
                direction="column"
                background="background-card"
                shadow="default"
                borderRadius
                padding={["md", "md", "sm", "md"]}
                margin={[false, false, "md", false]}>
                <TaskList
                  activeTaskId={activeTask?.id}
                  tasks={tasks}
                  working={working}
                  page={payload?.number}
                  pages={payload?.totalPages}
                  size={payload?.size}
                  total={payload?.totalElements}
                  onListItemClick={task => history.push(router.get("tasks", { taskId: task.id }, meta).path)}
                  onPageChange={changePage}
                />
              </Section>
            </Flex>

            <Flex width={{ xxs: 8 }} direction="column" innerMargin="md">
              {activeTask && <TaskDetails task={activeTask} refresh={fetch} onAction={handleAction} />}
            </Flex>
          </Flex>
        )}
      </Container>
    </React.Fragment>
  );
});
